import { AppButton } from '@/ui/buttons';
import { AppCalendar } from '@/ui/calendar';
import { nextWorkingDay } from '@/utils/helpers/dates.helper';
import moment from 'moment';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

type AutoScheduleModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (simulationsPerYear: number, startDate: Date) => void;
};

const StyledDialog = styled(Dialog)`
  width: 480px;
  max-width: 90vw;

  label {
    font-weight: 400;
  }

  .p-dialog-header {
    padding: var(--default-padding) var(--default-padding) 0
      var(--default-padding);
  }

  .p-dialog-content {
    padding: var(--medium-padding) var(--default-padding) var(--default-padding);
  }
`;

const Description = styled.p`
  font-size: var(--small-font-size);
  color: var(--black-main);
  margin: 0 0 var(--default-padding) 0;
  line-height: 1.4;
`;

const StyledFooter = styled.div`
  display: flex;
  justify-content: flex-start;
  gap: var(--xsmall-padding);
  padding-top: var(--default-padding);
`;

const DEFAULT_SIMULATIONS_PER_YEAR = 8;

const tomorrow = () => moment().add(1, 'day').startOf('day').toDate();
const defaultStartDate = () => nextWorkingDay(tomorrow());

export const AutoScheduleModal: React.FC<AutoScheduleModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation();
  const [simulationsPerYear, setSimulationsPerYear] = useState(
    DEFAULT_SIMULATIONS_PER_YEAR,
  );
  const [startDate, setStartDate] = useState<Date>(defaultStartDate());

  const simulationOptions = Array.from({ length: 24 }, (_, i) => ({
    label: t('phishing.autoSchedule.modal.simulationsPerYear.count', {
      count: i + 1,
    }),
    value: i + 1,
  }));

  const handleClose = () => {
    setSimulationsPerYear(DEFAULT_SIMULATIONS_PER_YEAR);
    setStartDate(defaultStartDate());
    onClose();
  };

  const handleConfirm = () => {
    onConfirm(simulationsPerYear, startDate);
    handleClose();
  };

  return (
    <StyledDialog
      visible={isOpen}
      onHide={handleClose}
      modal
      draggable={false}
      resizable={false}
      className="v2"
      header={t('phishing.autoSchedule.modal.title')}
    >
      <Description>{t('phishing.autoSchedule.modal.description')}</Description>

      <div className="field w-full">
        <label htmlFor="simulations-per-year">
          {t('phishing.autoSchedule.modal.simulationsPerYear.label')}
        </label>
        <Dropdown
          inputId="simulations-per-year"
          value={simulationsPerYear}
          options={simulationOptions}
          onChange={(e) => setSimulationsPerYear(e.value)}
          className="w-full"
        />
      </div>

      <div className="field w-full">
        <label htmlFor="auto-schedule-start-date">
          {t('phishing.autoSchedule.modal.startDate.label')}
        </label>
        <AppCalendar
          inputId="auto-schedule-start-date"
          value={startDate}
          onChange={(e) => e.value && setStartDate(e.value as Date)}
          minDate={tomorrow()}
          className="w-full"
          showIcon
          iconPos="left"
        />
      </div>

      <StyledFooter>
        <AppButton
          label={t('phishing.autoSchedule.modal.startButton')}
          onClick={handleConfirm}
        />
        <AppButton
          label={t('phishing.autoSchedule.modal.cancelButton')}
          severity="secondary"
          type="outlined"
          onClick={handleClose}
        />
      </StyledFooter>
    </StyledDialog>
  );
};
